import {
  Avatar,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import { IOrder, IPrescription } from 'API';
import { PrescriptionMedicationsList } from './PrescriptionMedicationsListDialog/index';
import { PrescriptionStatus } from './PrescriptionStatus/index';
import whiteExitIcon from 'Images/WhiteExit.svg';
import { instanceOfIPrescription } from 'Utils/instanceOfIPrescription';
import { ContactUsMobile, PrescriptionDialogStyle } from 'Components';
import { Exit } from 'Images';
import { Fragment } from 'react';
import { isSafari } from 'react-device-detect';

interface IPrescriptionDialogProps {
  onClose: () => void;
  open: boolean;
  item: IPrescription | IOrder;
}

export const PrescriptionDialog = (props: IPrescriptionDialogProps) => {
  const isDesktop = useIsDesktop();

  const dictionary = useDictionary(
    Dictionary,
    DICTIONARY.MedicationsAndPrescriptionsLobby
  );
  const generalDictionary = useDictionary(Dictionary, DICTIONARY.General);

  const { onClose, open, item } = props;

  const showTextField = (item: IOrder | IPrescription) => {
    if (!instanceOfIPrescription(item)) {
      return Boolean(item.notes);
    } else {
      return Boolean(item.eligibleNotes);
    }
  };

  return (
    <PrescriptionDialogStyle open={open} onClose={onClose}>
      <DialogTitle
        sx={(theme) => ({
          padding: '35px 24px 16px 24px',
          background: '#FFF',
          height: item.code !== 1 ? '135px' : '91px',
          [theme.breakpoints.up('sm')]: {
            height: '92px',
            paddingRight: '40px',
            paddingLeft: '40px',
            padding: '0 40px',
            background: '#39546A',
          },
        })}
      >
        <Grid
          container
          direction={'row'}
          alignItems={'center'}
          justifyContent={'space-between'}
          sx={{ height: '100%' }}
        >
          <Typography
            variant='h2'
            sx={(theme) => ({
              fontWeight: 700,
              lineHeight: '28px',
              color: '#39546A',
              fontSize: 24,
              alignSelf: 'flex-end',
              [theme.breakpoints.up('sm')]: {
                fontWeight: 400,
                color: 'white',
                alignSelf: 'center',
              },
            })}
          >
            {item.code !== 1
              ? `${dictionary.prescription} ${item.code}`
              : dictionary.prescription}
          </Typography>
          <Avatar
            alt={generalDictionary.closeWindow}
            src={isDesktop ? whiteExitIcon : Exit}
            onClick={onClose}
            sx={{
              width: 18,
              height: 18,
              cursor: 'pointer',
              alignSelf: isDesktop ? '' : 'flex-start',
              top: !isDesktop && isSafari ? '30px' : '',
            }}
          />
        </Grid>
      </DialogTitle>
      <DialogContent
        sx={(theme) => ({
          paddingRight: '24px',
          paddingLeft: '24px',
          backgroundColor: 'white',
          [theme.breakpoints.up('sm')]: {
            paddingRight: '64px',
            paddingLeft: '64px',
            paddingBottom: '71px',
          },
        })}
      >
        <PrescriptionStatus item={item} />
        <PrescriptionMedicationsList
          prescriptionMedications={item.medications}
          prescriptionStatuses={item.statuses}
          currentStatus={item.currentStatus}
        />
        {showTextField(item) && (
          <>
            <Typography fontWeight={700} variant='h3' mt={7}>
              {dictionary.orderComments}
            </Typography>
            <Typography
              sx={{
                width: '100%',
                wordWrap: 'break-word',
                display: 'inline-block',
              }}
            >
              {!instanceOfIPrescription(item)
                ? item.notes.split('|').map((note) => (
                    <Fragment key={note}>
                      {note}
                      <br />
                    </Fragment>
                  ))
                : item.eligibleNotes?.split('|').map((note) => (
                    <Fragment key={note}>
                      {note}
                      <br />
                    </Fragment>
                  ))}
            </Typography>
          </>
        )}
        {!isDesktop && (
          <ContactUsMobile
            title={
              item.isManual
                ? dictionary.contactUs1
                : dictionary.haveQuestionsAboutSupply
            }
            description={
              item.isManual
                ? dictionary.weWillAnswerAtNumber
                : dictionary.superPharmForYourSupport
            }
            phoneNumber={
              item.isManual
                ? dictionary.manualSupplyPhone
                : dictionary.superPharmPhone
            }
            phoneNumberTel={
              item.isManual
                ? dictionary.manualSupplyPhoneTel
                : dictionary.superPharmPhone
            }
          />
        )}
      </DialogContent>
    </PrescriptionDialogStyle>
  );
};
